import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { skip, Subscription } from 'rxjs';

import { AppNetworkService } from '../../shared/network/app-network.service';
import { DisableCopyDirective } from '../../shared/directives/disable-copy.directive';
import { MaxCharsDirective } from '../../shared/directives/max-chars.directive';
import { ChatbotService } from '../../chatbot-core/chatbot.service';
import { EquationEditorComponent } from '../../shared/equation-editor/equation-editor.component';
import { AppModalComponent } from '../../shared/modal/modal.component';
import { InputService } from './input.service';
import { IConfirmationService } from '../../shared/confirmation/confirmation.service.interface';
import { InputEquationService } from '../../shared/equation/input-equation.service';
import { ATEquation } from '../../shared/equation-editor/equation.models';
import { MathJaxConfigService } from '../../shared/latex/mathjax-config.service';
import { HighlightMathSelectionDirective } from '../../shared/directives/highlight-math-selection.directive';
import { EquationService } from '../../shared/equation-editor/equation.service';
import { ChatbotMode } from '../../chatbot-core/chatbot-api/chatbot-mode.model';
import { ChatbotHistoryService } from '../history/chatbot-history.service';


@Component({
  selector: 'sabis-chatbot-input',
  imports: [
    FormsModule,
    DisableCopyDirective,
    MaxCharsDirective,
    EquationEditorComponent,
    AppModalComponent,
    HighlightMathSelectionDirective
  ],
  templateUrl: './input.component.html',
  styleUrl: './input.component.scss'
})
export class SabisChatbotInputComponent
  implements OnInit, OnDestroy, AfterViewInit, OnChanges {
  @Input('maxChars') maxChars: number = 600;
  @Input('canAddEquation') canAddEquation: boolean = true;
  @Input('isConnectionEstablished') isConnectionEstablished: boolean = false;
  @Input('isChatbotModeActive') isChatbotModeActive: boolean = true;
  @Input('chatbotModes') chatbotModes: ChatbotMode[] = [];
  @Input('isOutOfPrompts') isOutOfPrompts: boolean = false;
  @Input('isOutOfSessions') isOutOfSessions: boolean = false;
  @Input('hasActiveSession') hasActiveSession: boolean = false;
  @Input('isLandingPage') isLandingPage: boolean = false;
  @Input('disclaimerMessage') disclaimerMessage: string =
    "I'm your learning AI companion. Please don't share any personal, sensitive, or confidential information with me. All my answers are inspired by your E-books. Always verify them!";

  @Output() sendMessage = new EventEmitter<void>();
  @Output() cancelEdit = new EventEmitter<string | null>();
  @Output() modeChange = new EventEmitter<string>();

  @ViewChild('messageBox')
  private _userMessageDiv!: ElementRef<HTMLTextAreaElement>;
  @ViewChild('maxCountDir') private _maxCountDirective!: MaxCharsDirective;

  private _healthCheckEventSub: Subscription = new Subscription();
  private _networkEventSub: Subscription = new Subscription();
  private _resendMessageEventSub: Subscription = new Subscription();
  private _regenerateResponseEventSub: Subscription = new Subscription();
  private _chatInProgressEventSub: Subscription = new Subscription();
  private _isResponseReadyEventSub: Subscription = new Subscription();
  private _isFirstMessage: boolean = true;
  private _currentMessageSub: Subscription = new Subscription();
  private _copyToInputSub: Subscription = new Subscription();

  public userMessage: string = '';
  public userMessageLength: number = 0;
  public showCounter: boolean = false;
  public showWarning: boolean = false;
  public isOverMaxChars: boolean = false;
  public showPlaceholder: boolean = true;
  public isChatInProgress: boolean = false;
  public errorMessage: string = '';
  public isNetworkOnline: boolean = false;
  public isMessageNotEmpty: boolean = false;
  public isConfirmDisabled: boolean = false;
  public isEditingMessage: boolean = false;
  public chatbotModeCode: string | null = null;
  private _charCount = 0;

  // Equation-related properties (managed by InputEquationService)
  public equationValue: ATEquation | null = null;
  public showEquationModal: boolean = false;
  @ViewChild('equationEditor') equationEditor!: EquationEditorComponent;

  constructor(
    @Inject('ChatbotDialogService') private _confirmationService: IConfirmationService,
    private _renderer: Renderer2,
    private _appNetworkService: AppNetworkService,
    private _chatbotService: ChatbotService,
    private _inputService: InputService,
    private _inputEquationService: InputEquationService,
    private _mathjaxConfService: MathJaxConfigService,
    private _equationService: EquationService,
    private _historyService: ChatbotHistoryService
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['chatbotModes'] && !this.chatbotModeCode) {
      this.initializeChatbotMode();
    }
  }

  ngAfterViewInit(): void {
    // restore last user message each time the component is created
    const lastMessage = this._inputService.getCurrentMessage();
    if (!!lastMessage) {
      this.restoreMessage(lastMessage);
      this.isEditingMessage = this._inputService.getIsEditingMessage();
    }

    // Listen for externally pushed messages.
    this._currentMessageSub = this._inputService.currentMessage$.subscribe((message) => {
      if (message) {
        this.restoreMessage(message);
      }
    });

    // Listen for text copied from bot responses to append to the input.
    this._copyToInputSub = this._inputService.appendToInput$.subscribe((text) => {
      if (this.isOutOfPrompts && this.isOutOfSessions) {
        this._inputService.clearPendingAppends();
        return;
      }
      this.appendTextToInput(text);
      this._inputService.clearPendingAppends();
    });

    // Flush copy requests that happened before the component was rendered
    for (const text of this._inputService.clearPendingAppends()) {
      if (!this.isOutOfPrompts && !this.isOutOfSessions) {
        this.appendTextToInput(text);
      }
    }
  }

  private restoreMessage(message: string): void {
    this._userMessageDiv.nativeElement.innerHTML = message;
    this._equationService.restoreEquationMathML(this._userMessageDiv.nativeElement);
    this._mathjaxConfService.typesetClear(this._userMessageDiv.nativeElement).then(() => {
      this._mathjaxConfService.typeset(this._userMessageDiv.nativeElement);
    });
    this._maxCountDirective?.inputChanged();
    this.checkPlaceholderVisibility();
    this.setFocus();
  }

  private appendTextToInput(html: string): void {
    if (this.isOutOfPrompts && this.isOutOfSessions) {
      this._inputService.clearPendingAppends();
      return;
    }
    // Replace all Unicode U+0020 with a space
    const sanitizedHtml = html.replace(/\u0020/g, ' ').replace(/\uE047/g, ' ').replace(/\uE007/g, ' ').replace(/\uE001/g, ' ');
    const el = this._userMessageDiv.nativeElement;
    // Strip residual <br> tags (left by browser after clearing a contenteditable)
    const hasContent = el.innerHTML.replace(/<br\s*\/?>/gi, '').trim().length > 0;
    const newHtml = hasContent ? el.innerHTML + '&nbsp;' + sanitizedHtml : sanitizedHtml;
    this.setInputValue(newHtml, true);
    this.setFocus();
  }

  ngOnInit(): void {
    // listen to network status: disable input if not connected
    this._networkEventSub = this._appNetworkService.online$.subscribe(
      (isOnline) => {
        this.isNetworkOnline = isOnline;
      }
    );

    this._chatInProgressEventSub = this._chatbotService.isChatInProgress$.subscribe((isChatInProgress) => {
      this.isChatInProgress = isChatInProgress;
    });

    this._isResponseReadyEventSub = this._chatbotService.isResponseReady$.subscribe((r) => {
      if (r) {
        if (this._isFirstMessage) {
          this.clearUserMessage();
          this._inputService.clearStoredMessage();
          this.showPlaceholder = true;
          this._isFirstMessage = false;
        }
      }
    });
    this.initializeChatbotMode();
  }

  public sendUserMessage(userText: string): void {
    this._userMessageDiv.nativeElement.innerHTML = userText;
    this.onSendMessage();
  }

  public editMessage(userText: string, messageId: string): void {
    this.setInputValue(userText);
    this.isMessageNotEmpty = true;
    this.isEditingMessage = true;
    this._inputService.setIsEditingMessage(true);
    this._inputService.setEditingMessageId(messageId);
  }

  private setInputValue(userText: string, allowOverflow: boolean = false): void {
    const el = this._userMessageDiv.nativeElement;
    el.innerHTML = userText;

    // Populate sabis-equation innerHTML from data-mathml so MathJax can render them
    this._equationService.restoreEquationMathML(el);

    // Render any LaTeX equations
    this._mathjaxConfService.typesetClear(el).then(() => {
      this._mathjaxConfService.typeset(el);
    });

    if (allowOverflow) {
      // Allow the content to exceed maxChars — directive skips truncation
      this._maxCountDirective?.inputChangedAllowOverflow();
      this.onInputChange();
    } else {
      // Force triggering of the inputChanged event after a message is edited.
      this.triggerInputChangedEvent();
    }
  }

  private triggerInputChangedEvent(): void {
    const event = new Event('input', { bubbles: true });
    this._userMessageDiv.nativeElement.dispatchEvent(event);
  }

  public onCancelEdit(): void {
    this.cancelEditedMessage();
    this.clearUserMessage();
    this._inputService.clearStoredMessage();
  }

  /**
  * Cancel the edited message to reset editing buttons state and message id
  * Editing state and edited message id are now stored in the input service
  * This allows us to restore the editing state and message id after minimize
  */
  private cancelEditedMessage(): void {
    const editingMessageId = this._inputService.getEditingMessageId();
    this.isEditingMessage = false;
    this._inputService.setIsEditingMessage(false);
    this._inputService.setEditingMessageId(null);
    this.cancelEdit.emit(editingMessageId);
  }

  /**
  * Sends the user's message to the chatbot service.
  * Processes the message content, handles MathJax rendering, and manages the chat flow.
  * Clears the input field and resets UI state upon successful message delivery.
  */
  public onSendMessage(): void {
    this.isEditingMessage = false;
    this._isFirstMessage = true;
    this.sendMessage.emit();
    this.isChatInProgress = true;
    const activeLlmSessionId = this._chatbotService.getActiveSessionId();

    let imagelessUserMessage = this._userMessageDiv.nativeElement.innerHTML;

    const cleanedHtml = imagelessUserMessage
      .replace(/^(&nbsp;|\s)+|(&nbsp;|\s)+$/g, '')
      .trim();

    if (this._charCount <= this.maxChars) {
      this._chatbotService.handleMessageFlow(cleanedHtml, this.chatbotModeCode!).subscribe({
        next: () => {
          if (this._isFirstMessage) {
            this.clearUserMessage();
            this._inputService.clearStoredMessage();
            this._inputService.clearPendingAppends();
            this._isFirstMessage = false;
            this.setFocus();
          }
        },
        error: () => {
          this.isChatInProgress = false;
        },
        complete: () => {
          this.isChatInProgress = false;
          this.showWarning = false;
          this.showCounter = false;
          this.showPlaceholder = true;
          this.isMessageNotEmpty = false;
          if (activeLlmSessionId) {
            this._historyService.notifyFirstMessageSent();
          }
          this.cancelEditedMessage();
        },
      });
    } else {
      this.isChatInProgress = false;
      this.showWarning = false;
      this.showCounter = false;
      this.showPlaceholder = true;
      this.isMessageNotEmpty = false;
      if (activeLlmSessionId) {
        this._historyService.notifyFirstMessageSent();
      }
      this.cancelEditedMessage();
    }
  }

  public onNewChat(): void {
    this._inputService.clearPendingAppends();
    this.isOutOfPrompts = false;
    this.isChatInProgress = false;
    this.clearUserMessage();
    this._inputService.clearStoredMessage();
    this.showPlaceholder = true;
    this.showWarning = false;
    this.showCounter = false;
    this.setFocus()
    this.setDefaultChatbotMode();
  }

  /**
   * check if current message content is valid to be sent to the chatbot
   * @param message
   * @returns
   */
  public isNotEmptyMessage(message: string): boolean {
    const htmlContent = this._userMessageDiv.nativeElement.innerHTML.trim();
    const containsImage = /<img\s+[^>]*src=["'][^"']+["'][^>]*>/i.test(
      htmlContent
    );

    // Add check for equation elements
    const containsEquation = /<sabis-equation[^>]*>/i.test(htmlContent);

    return message.trim().length > 0 || containsImage || containsEquation;
  }

  /**
   * "Enter" to send message
   * @param event
   */
  public onStopSending(): void {
    //TO DO in future story
  }

  /**
   * callback to toggle the max-char limit warning
   * @param event
   */
  public handleCharLimit(event: Event): void {
    const limitReached = (event as CustomEvent<boolean>).detail;
    this.showWarning = limitReached;
  }

  public onCharCount(event: Event): void {
    const count = (event as CustomEvent<number>).detail;
    const messageBox = this._userMessageDiv.nativeElement;
    const text = messageBox.innerText;
    this._charCount = count;

    this.userMessageLength = count;
    this.showCounter = count >= this.maxChars * 0.975;
    this.isOverMaxChars = count > this.maxChars;


    this.userMessage = text;
    this.isMessageNotEmpty = this.isNotEmptyMessage(text);
    if (!this.isMessageNotEmpty) {
      this.cancelEditedMessage();
    }
  }

  // ****************************************************** Equation ******************************************************

  /**
   * listen to clicks on user message div
   * @param event
   */
  public onInputDoubleClick(event: MouseEvent): void {
    let equationElement = this._inputEquationService.tryGetEquationElement(event);
    // edit equation
    if (equationElement) {
      this.equationValue = this._inputEquationService.getATEquation(equationElement);
      this._inputEquationService.startEditEquation(equationElement);
      this.showEquationModal = true;
      this.isConfirmDisabled = false;
    }
  }

  /** show equation modal */
  public openEquationModal() {
    // stop is max chars
    if (this.showWarning) {
      return;
    }
    this.showEquationModal = true;
    this.equationValue = null;
    this.isConfirmDisabled = false;
  }

  public onEquationKeyDown(event: KeyboardEvent): void {
    this._inputEquationService.handleEquationKeyDown(event, {
      onConfirm: () => this.confirmEquationModal(),
      onClose: () => this.closeEquationModal(),
    });
  }

  /** called when equation modal confirmed */
  public async confirmEquationModal(): Promise<void> {
    this.isConfirmDisabled = true;
    const result = await this._inputEquationService.confirmEquation(
      this.equationEditor,
      this._userMessageDiv,
      this._renderer,
      this._confirmationService
    );
    this.isConfirmDisabled = false;

    if (result.isEmpty === false) {
      this.equationValue = null;
      this.showEquationModal = false;
      this._maxCountDirective.inputChanged();
      this.checkPlaceholderVisibility();
      this.cacheMessage();
    }
  }

  /** called when equation modal closed without confirmation */
  public closeEquationModal() {
    this._inputEquationService.closeEquationModal();
    this.equationValue = null;
    this.showEquationModal = false;
  }

  public onInputChange(): void {
    this.checkPlaceholderVisibility();
    this.cacheMessage();
  }

  public checkPlaceholderVisibility(): void {
    const el = this._userMessageDiv.nativeElement;
    const isEmpty = !this.isNotEmptyMessage(this.userMessage);

    if (isEmpty) {
      el.classList.add('placeholder-visible');
    } else {
      el.classList.remove('placeholder-visible');
    }
  }

  /** store html of user message currently in the div */
  private cacheMessage(): void {
    this._inputService.setCurrentMessage(
      this._userMessageDiv.nativeElement.innerHTML
    );
  }

  public onBeforeInput(event: InputEvent): void {
    // delete equation if any
    if (event.inputType === 'deleteContentBackward') {
      this._inputEquationService.deleteEquation(event);
    }
  }

  public onKeyDown(event: KeyboardEvent): void {

    // Prevent arrow keys from bubbling to slider, without this code the arrows will move the page scrollbars
    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight' ||
      event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      event.stopPropagation();
    }

    if (
      event.key === 'Enter' &&
      !event.shiftKey &&
      !this._chatbotService.isTablet() &&
      !this.isChatInProgress
    ) {
      event.preventDefault();
      if (this.isNotEmptyMessage(this.userMessage)) {
        this.onSendMessage();
      }
    }

    if (event.key === 'Backspace') {
      this._inputEquationService.deleteEquation(event);
    }
  }

  public setFocus(): void {
    setTimeout(() => {
      const chatbotInput = document.querySelector('.chat-input-field') as HTMLElement;
      if (chatbotInput) {
        chatbotInput.focus();

        // Set cursor position
        const range = document.createRange();
        const selection = window.getSelection();

        if (chatbotInput.childNodes.length > 0) {
          range.selectNodeContents(chatbotInput);
          range.collapse(false);
        } else {
          range.setStart(chatbotInput, 0);
          range.setEnd(chatbotInput, 0);
        }

        selection?.removeAllRanges();
        selection?.addRange(range);
      }
    }, 100);
  }

  private clearUserMessage(): void {
    this.userMessage = '';
    this._userMessageDiv.nativeElement.innerHTML = '';
    this.isMessageNotEmpty = false;
    this.isOverMaxChars = false;
    this.showPlaceholder = true;
    this.showWarning = false;
    this.showCounter = false;
  }

  private initializeChatbotMode(): void {
    // Try to restore the previously selected mode from service
    const storedModeCode = this._inputService.getSelectedChatbotModeCode();
    if (storedModeCode && this.chatbotModes.some(mode => mode.code === storedModeCode)) {
      this.chatbotModeCode = storedModeCode;
    } else {
      // If no stored mode or stored mode is invalid, set to default
      this.setDefaultChatbotMode();
    }
  }

  private setDefaultChatbotMode(): void {
    const defaultMode = this.chatbotModes.find(mode => mode.isDefault);
    if (defaultMode) {
      this.chatbotModeCode = defaultMode.code;
      this._inputService.setSelectedChatbotModeCode(defaultMode.code);
    }
  }

  public setChatbotModeCode(modeCode: string): void {
    if (this.chatbotModeCode !== modeCode) {
      this.chatbotModeCode = modeCode;
      this._inputService.setSelectedChatbotModeCode(modeCode);
      this.modeChange.emit(modeCode);
    }
  }

  /**
   * Inserts pasted clipboard content as plain text only.
   *
   * Prevents the browser's default rich-HTML paste behavior, which could insert
   * image tags or other unsupported markup into the contenteditable chat input.
   * It preserves the current selection and dispatches an input event afterward
   * so the existing character-count, placeholder, and message-cache handlers run.
   */
  public onPaste(event: ClipboardEvent): void {
    // Blocks the browser's default paste entirely
    event.preventDefault();

    // Selection is needed because the input is a contenteditable <div>, not a textarea.
    const selection = window.getSelection();
    if (!selection?.rangeCount) {
      return;
    }

    // Get the current selection range within the contenteditable div.
    const range = selection.getRangeAt(0);
    // Create a new text node containing the plain text from the clipboard.
    const textNode = document.createTextNode(
      event.clipboardData?.getData('text/plain') ?? ''
    );

    // Delete the current selection contents and insert the new plain text node.
    /**
     * For example, if the user highlights world in Hello world and pastes there, 
     * the expected result is Hello there. Without deleteContents() 
     * the pasted text would be inserted while world remains.
     */
    range.deleteContents();
    range.insertNode(textNode);

    range.setStartAfter(textNode);
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);

    /**
     * fix issue where
     * the warning can appear,
     * but the pasted text disappears,
     * or the old content comes back.
     */
    this._maxCountDirective?.inputChangedAllowOverflow();
    this.onInputChange();
  }

  /**
   * Handles the drop event for the contenteditable chat input.
   *
   * Inserts the dropped text at the current selection, replacing any selected content.
   *
   * @param event The drag event containing the dropped data.
   * @returns void
   */
  public onDrop(event: DragEvent): void {
    event.preventDefault();

    var text = event.dataTransfer?.getData('text/plain') ?? '';
    
    // This was added to remove the text if files are dropped, as we only want plain text.
    if(event.dataTransfer!.files?.length > 0) {
      text = "";
    }

    const selection = window.getSelection();
    if (!selection?.rangeCount) {
      return;
    }

    const range = selection.getRangeAt(0);
    const textNode = document.createTextNode(text);
    range.deleteContents();
    range.insertNode(textNode);

    range.setStartAfter(textNode);
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);

    this._maxCountDirective?.inputChangedAllowOverflow();
    this.onInputChange();
  }

  ngOnDestroy(): void {
    this._healthCheckEventSub.unsubscribe();
    this._networkEventSub.unsubscribe();
    this._resendMessageEventSub.unsubscribe();
    this._regenerateResponseEventSub.unsubscribe();
    this._chatInProgressEventSub.unsubscribe();
    this._isResponseReadyEventSub.unsubscribe();
    this._currentMessageSub.unsubscribe();
    this._copyToInputSub.unsubscribe();
  }
}
